'use client';

import * as React from 'react';
import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import {
  Pagination as PaginationRoot,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/hen-ui/shadcn/pagination';
import { cn } from '@/hen-ui/utils';

export type PaginationMode = 'link' | 'client';

export interface PaginationProps {
  /** 1-indexed current page. */
  currentPage: number;
  totalPages: number;
  /** `link` navigates via href; `client` invokes `onPageChange`. */
  mode?: PaginationMode;
  /** Required when `mode="link"`. */
  getPageUrl?: (page: number) => string;
  /** Required when `mode="client"`. */
  onPageChange?: (page: number) => void;
  /** Optional link primitive (e.g. Next.js `Link`) for `mode="link"`. */
  linkComponent?: React.ElementType;
  /** Visible word on the previous control. Defaults to Previous. Empty leaves the chevron. */
  previousLabel?: string;
  /** Visible word on the next control. Defaults to Next. Empty leaves the chevron. */
  nextLabel?: string;
  className?: string;
  /** Landmark name. Defaults to "pagination". Two pagers on one page need different names. */
  'aria-label'?: string;
}

/** Build a compact page list with ellipsis for long ranges. */
export function getPaginationRange(
  currentPage: number,
  totalPages: number,
): Array<number | 'ellipsis'> {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages = new Set<number>([
    1,
    totalPages,
    currentPage,
    currentPage - 1,
    currentPage + 1,
  ]);

  const sorted = Array.from(pages)
    .filter((page) => page >= 1 && page <= totalPages)
    .sort((a, b) => a - b);

  const range: Array<number | 'ellipsis'> = [];
  let previous = 0;

  for (const page of sorted) {
    if (previous && page - previous > 1) {
      range.push('ellipsis');
    }
    range.push(page);
    previous = page;
  }

  return range;
}

type PageAnchorProps = {
  href: string;
  isActive?: boolean;
  ariaLabel?: string;
  children: React.ReactNode;
  onNavigate?: () => void;
  linkComponent?: React.ElementType;
};

function PageAnchor({
  href,
  isActive,
  ariaLabel,
  children,
  onNavigate,
  linkComponent: LinkComponent = 'a',
}: PageAnchorProps) {
  if (onNavigate) {
    return (
      <PaginationLink
        href='#'
        isActive={isActive}
        aria-label={ariaLabel}
        data-hen-id={COMPONENT_IDS.Pagination}
        onClick={(event) => {
          event.preventDefault();
          onNavigate();
        }}
      >
        {children}
      </PaginationLink>
    );
  }

  return (
    <PaginationLink asChild isActive={isActive} aria-label={ariaLabel}>
      <LinkComponent href={href} data-hen-id={COMPONENT_IDS.Pagination}>
        {children}
      </LinkComponent>
    </PaginationLink>
  );
}

/**
 * Numbered pagination control (shadcn-style) for category hubs and article lists.
 */
export function Pagination({
  currentPage,
  totalPages,
  mode = 'link',
  getPageUrl,
  onPageChange,
  linkComponent: LinkComponent = 'a',
  previousLabel = 'Previous',
  nextLabel = 'Next',
  className,
  'aria-label': ariaLabel,
}: PaginationProps) {
  if (totalPages <= 1) return null;

  const isClient = mode === 'client';
  if (!isClient && !getPageUrl) return null;
  if (isClient && !onPageChange) return null;

  const safePage = Math.min(Math.max(1, currentPage), totalPages);
  const range = getPaginationRange(safePage, totalPages);

  const hrefFor = (page: number) => (getPageUrl ? getPageUrl(page) : '#');
  const goTo = (page: number) => {
    if (page !== safePage) onPageChange?.(page);
  };

  const prevDisabled = safePage <= 1;
  const nextDisabled = safePage >= totalPages;

  const renderPrevNext = (
    kind: 'previous' | 'next',
    targetPage: number,
    disabled: boolean,
  ) => {
    const Shell = kind === 'previous' ? PaginationPrevious : PaginationNext;
    const label = kind === 'previous' ? previousLabel : nextLabel;
    const visibleLabel = label.trim();
    const ariaLabel =
      visibleLabel ||
      (kind === 'previous' ? 'Go to previous page' : 'Go to next page');

    if (disabled) {
      return (
        <Shell
          href='#'
          label={label}
          aria-label={ariaLabel}
          aria-disabled='true'
          tabIndex={-1}
          className='pointer-events-none opacity-50'
          onClick={(event) => event.preventDefault()}
        />
      );
    }

    if (isClient) {
      return (
        <Shell
          href='#'
          label={label}
          aria-label={ariaLabel}
          data-hen-id={COMPONENT_IDS.Pagination}
          onClick={(event) => {
            event.preventDefault();
            goTo(targetPage);
          }}
        />
      );
    }

    return (
      <Shell
        href={hrefFor(targetPage)}
        label={label}
        aria-label={ariaLabel}
        data-hen-id={COMPONENT_IDS.Pagination}
      />
    );
  };

  return (
    <PaginationRoot
      className={cn(className)}
      data-hen-id={COMPONENT_IDS.Pagination}
      {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
    >
      <PaginationContent>
        <PaginationItem>
          {renderPrevNext('previous', safePage - 1, prevDisabled)}
        </PaginationItem>

        {range.map((entry, index) =>
          entry === 'ellipsis' ? (
            <PaginationItem key={`ellipsis-${index}`}>
              <PaginationEllipsis />
            </PaginationItem>
          ) : (
            <PaginationItem key={entry}>
              {isClient ? (
                <PageAnchor
                  href='#'
                  isActive={entry === safePage}
                  ariaLabel={`Page ${entry}`}
                  onNavigate={() => goTo(entry)}
                >
                  {entry}
                </PageAnchor>
              ) : (
                <PageAnchor
                  href={hrefFor(entry)}
                  isActive={entry === safePage}
                  ariaLabel={`Page ${entry}`}
                  linkComponent={LinkComponent}
                >
                  {entry}
                </PageAnchor>
              )}
            </PaginationItem>
          ),
        )}

        <PaginationItem>
          {renderPrevNext('next', safePage + 1, nextDisabled)}
        </PaginationItem>
      </PaginationContent>
    </PaginationRoot>
  );
}
